import React, { useState, useContext } from "react";
import { Box, Typography, Paper, TextField, Button } from "@mui/material";
import DataComparison from "./DataComparison";
import InventorySection from "./InventorySection";
import TagsField from "./TagsField";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import ConfirmSaveDialog from "../sections/receiving/ConfirmSaveDialog";
import ProductStatus from "../sections/receiving/ProductStatus";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import CategorySelector from "../components/CategorySelector";
import SizeSelector from "../components/SizeSelector";

const ProductImage = ({ image }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        textAlign: "center",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {image ? (
        <Box
          component="img"
          src={image}
          alt="Product"
          sx={{
            width: "100%",
            height: "auto",
            maxHeight: "600px",
            objectFit: "contain",
            borderRadius: 1,
          }}
        />
      ) : (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            backgroundColor: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            border: "2px dashed #ddd",
          }}
        >
          <Typography variant="body2" color="textSecondary">
            No image available
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const ProductDetails = ({
  selectedCin7Item,
  selectedShopifyB2cItem,
  selectedShopifyB2bItem,
  onProductUpdate,
}) => {
  const [editValues, setEditValues] = useState({});
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const context = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const handleSaveAll = () => {
    if (editValues.productName === "") {
      context.showMessage("Product Name is required", "error");
      return;
    }
    if (editValues.supplierInventoryCode === "") {
      context.showMessage("Unit Per Case is required", "error");
      return;
    }
    setShowSaveConfirmation(true);
  };

  const confirmSave = async () => {
    setIsSaving(true);
    try {
      const {
        size,
        productName,
        barcode,
        category,
        supplierInventoryCode,
        tags,
      } = editValues;

      const requestBody = {
        Shopify: {
          b2c: selectedShopifyB2cItem
            ? {
                productId: selectedShopifyB2cItem.productId,
                variantId: selectedShopifyB2cItem.variantId,
              }
            : undefined,
          b2b: selectedShopifyB2bItem
            ? {
                productId: selectedShopifyB2bItem.productId,
                variantId: selectedShopifyB2bItem.variantId,
              }
            : undefined,
        },
        cin7Id: selectedCin7Item?.productId,
        ...(productName !== undefined
          ? { productName }
          : selectedCin7Item?.productName
            ? { productName: selectedCin7Item.productName }
            : {}),
        ...(barcode !== undefined
          ? { barcode }
          : selectedCin7Item?.barcode
            ? { barcode: selectedCin7Item.barcode }
            : {}),
        ...(size && { size }),
        ...(category && { category }),
        ...(supplierInventoryCode && { unitPerCase: supplierInventoryCode }),
        ...(tags && { tags }),
      };

      const response = await fetchWithInterceptor(
        "/api/receiving/update-product-fields",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.error || `HTTP ${response.status}: Error updating in Shopify`
        );
      }

      context.showMessage("Product updated successfully", "success");

      if (onProductUpdate) {
        onProductUpdate(result);
      }

      setIsEditingMode(false);
      setEditValues({});
      setShowSaveConfirmation(false);
    } catch (error) {
      context.showMessage(`Error saving: ${error.message}`, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelAll = () => {
    setIsEditingMode(false);
    setEditValues({});
  };

  const renderEditableField = (fieldName, currentValue, label) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 0.5,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
          {label}:
        </Typography>

        {isEditingMode ? (
          <TextField
            size="small"
            value={
              editValues[fieldName] !== undefined
                ? editValues[fieldName]
                : (currentValue ?? "")
            }
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [fieldName]: e.target.value,
              }))
            }
            sx={{
              minWidth: 120,
              width: "100%",
              "& input": { fontSize: 14 },
            }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, minWidth: 120, width: "100%" }}
          >
            {currentValue ?? "-"}
          </Typography>
        )}
      </Box>
    );
  };

  if (!selectedCin7Item?.productId || !selectedShopifyB2cItem?.productId) {
    return null;
  }
  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "stretch",
          minHeight: "600px",
        }}
      >
        <Box sx={{ width: "40%" }}>
          <ProductImage image={selectedShopifyB2cItem?.images} />
        </Box>

        <Box sx={{ width: "60%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <DataComparison
              cin7Value={selectedCin7Item?.productName}
              shopifyValue={selectedShopifyB2cItem?.productName}
              label="Product Name"
              isEditingMode={isEditingMode}
              editValue={
                editValues.productName !== undefined
                  ? editValues.productName
                  : (selectedCin7Item?.productName ??
                    selectedShopifyB2cItem?.productName ??
                    "")
              }
              setEditValue={(value) =>
                setEditValues((prev) => ({ ...prev, productName: value }))
              }
              fieldName="productName"
              hideLabel={true}
            />
            {!isEditingMode ? (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsEditingMode(true)}
                sx={{ width: 100 }}
              >
                EDIT
              </Button>
            ) : (
              <>
                <Button
                  color="primary"
                  onClick={handleCancelAll}
                  sx={{ width: 100, mr: 1 }}
                  disabled={isSaving}
                >
                  CANCEL
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleSaveAll}
                  sx={{ width: 100 }}
                  disabled={isSaving}
                >
                  {isSaving ? "SAVING..." : "SAVE"}
                </Button>
              </>
            )}
          </Box>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <DataComparison
                cin7Value={selectedCin7Item?.sku}
                shopifyValue={selectedShopifyB2cItem?.sku}
                label="SKU"
                isEditingMode={false}
                fieldName="SKU"
              />
              <DataComparison
                cin7Value={selectedCin7Item?.barcode}
                shopifyValue={selectedShopifyB2cItem?.barcode}
                label="Barcode"
                isEditingMode={isEditingMode}
                editValue={
                  editValues.barcode !== undefined
                    ? editValues.barcode
                    : (selectedCin7Item?.barcode ??
                      selectedShopifyB2cItem?.barcode ??
                      "")
                }
                setEditValue={(value) =>
                  setEditValues((prev) => ({ ...prev, barcode: value }))
                }
                fieldName="barcode"
                autoFocus={isEditingMode}
              />

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, p: 0.5 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, minWidth: 120 }}
                >
                  Category:
                </Typography>

                {isEditingMode ? (
                  <CategorySelector
                    value={
                      editValues.category !== undefined
                        ? editValues.category
                        : (selectedCin7Item?.category ?? "")
                    }
                    onChange={(value) =>
                      setEditValues((prev) => ({ ...prev, category: value }))
                    }
                    hiddenLabel
                  />
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedCin7Item?.category ?? "-"}
                  </Typography>
                )}
              </Box>
              {renderEditableField(
                "supplierInventoryCode",
                selectedCin7Item?.supplierInventoryCode,
                "Unit Per Case"
              )}

              <TagsField
                value={
                  editValues.tags !== undefined
                    ? editValues.tags
                    : selectedCin7Item?.tags || ""
                }
                onChange={(value) =>
                  setEditValues((prev) => ({ ...prev, tags: value }))
                }
                isEditingMode={isEditingMode}
                disablePadding={false}
                label="Tags"
              />

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, p: 0.5 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, minWidth: 120 }}
                >
                  Size:
                </Typography>

                {isEditingMode ? (
                  <SizeSelector
                    value={
                      editValues.size !== undefined
                        ? editValues.size
                        : (selectedShopifyB2cItem?.size ?? "")
                    }
                    onChange={(value) =>
                      setEditValues((prev) => ({ ...prev, size: value }))
                    }
                    hiddenLabel
                  />
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedShopifyB2cItem?.size ?? "-"}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, p: 0.5 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, minWidth: 120 }}
                >
                  Status:
                </Typography>
                <ProductStatus status={selectedShopifyB2cItem?.status} />
              </Box>
            </Box>
          </Paper>

          {/* New Inventory Section */}
          <InventorySection
            cin7Data={selectedCin7Item}
            shopifyInventory={{
              unavailable: selectedShopifyB2cItem?.unavailable || 0,
              committed: selectedShopifyB2cItem?.allocated || 0,
              available: selectedShopifyB2cItem?.available || 0,
              onHand: selectedShopifyB2cItem?.onHand || 0,
            }}
          />
        </Box>
      </Box>

      <ConfirmSaveDialog
        open={showSaveConfirmation}
        onClose={() => setShowSaveConfirmation(false)}
        onConfirm={confirmSave}
        loading={isSaving}
      />
    </>
  );
};

export default ProductDetails;
